/**
 * PanelStepper — a dropdown with ‹ › arrows on either side.
 *
 * For an ordered list with too many word labels to sit side by side in a SegmentedToggle
 * (six named distance presets get ~35 px each). The arrows are the fast path — step one notch
 * without reading the list — and the centre still opens the full list for a direct jump.
 *
 * Arrows stop at the ends rather than wrapping: these lists are ordered scales, and wrapping from
 * Extreme back to Tiny with one tap is a mis-tap waiting to happen.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CONTROL_H, TRACK, ACCENT_TEXT, MOTION } from './tokens';
import { OptionList, type OptionItem } from './OptionList';

interface PanelStepperProps<T> {
  options: readonly OptionItem<T>[];
  /** Selected value; anything not in `options` shows a dash and starts the arrows from the ends. */
  value: T;
  onSelect: (value: T) => void;
  ariaLabel?: string;
}

export function PanelStepper<T extends string | number>({
  options, value, onSelect, ariaLabel,
}: PanelStepperProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [open]);

  const index = options.findIndex((o) => o.value === value);
  const step = (delta: number) => {
    const next = index < 0 ? (delta > 0 ? 0 : options.length - 1) : index + delta;
    if (next < 0 || next >= options.length) return;
    onSelect(options[next].value);
  };

  const arrow = (dir: -1 | 1, disabled: boolean) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => step(dir)}
      aria-label={dir < 0 ? 'Previous' : 'Next'}
      className={`shrink-0 w-8 h-full rounded-full flex items-center justify-center ${MOTION} ${
        disabled ? 'text-neutral-300' : `${ACCENT_TEXT} cursor-pointer hover:bg-white active:bg-neutral-50`
      }`}
    >
      {dir < 0 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
    </button>
  );

  return (
    <div ref={ref} className="relative">
      <div
        className={`${CONTROL_H} ${TRACK} p-1 flex items-center gap-1`}
        role="group"
        aria-label={ariaLabel}
      >
        {arrow(-1, index <= 0)}
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={`flex-1 min-w-0 h-full rounded-full text-xs md:text-[11px] font-bold text-neutral-800
            cursor-pointer hover:bg-white active:bg-neutral-50 ${MOTION}`}
        >
          <span className="block truncate px-1">{options[index]?.label ?? '—'}</span>
        </button>
        {arrow(1, index >= 0 && index >= options.length - 1)}
      </div>

      {open && (
        <OptionList
          options={options}
          value={value}
          onSelect={onSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
